import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CheckCircle } from "@phosphor-icons/react";

const INDUSTRIES = ["Trading / Distribution", "Retail", "Manufacturing", "Services", "Professional / CA firm", "E-commerce"];

export default function Setup() {
  const { loadCompanies, user } = useApp();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    legal_name: "", gstin: "", pan: "", address: "", city: "",
    fy_start: "2026-04-01", industry: INDUSTRIES[0],
  });
  const [gstInfo, setGstInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async () => {
    if (!form.gstin) return;
    try {
      const { data } = await api.get(`/companies/gstin/${form.gstin.toUpperCase()}`);
      setGstInfo(data);
      setForm((f) => ({ ...f, pan: data.pan || f.pan }));
      toast.success(`GSTIN valid — ${data.state}`);
    } catch (e) {
      setGstInfo(null);
      toast.error(apiError(e));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/companies", { ...form, gstin: form.gstin.toUpperCase() || null, seed_coa: true });
      localStorage.setItem("fx_company", data.id);
      await loadCompanies();
      toast.success("Company created with Indian chart of accounts");
      navigate("/");
      window.location.reload();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-12">
      <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-muted-foreground">Onboarding</p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">Company setup</h1>
      <p className="mt-2 max-w-xl text-sm text-muted-foreground">
        Signed in as {user?.email}. We will seed a full Indian chart of accounts, GST duty ledgers and a default
        warehouse so you can raise your first invoice in under 4 minutes.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-6 border border-border p-6" data-testid="company-setup-form">
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Legal name *</Label>
            <Input required data-testid="company-name-input" value={form.legal_name}
              onChange={(e) => setForm({ ...form, legal_name: e.target.value })} placeholder="Sharma Traders Pvt Ltd" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest">GSTIN</Label>
            <div className="flex gap-2">
              <Input data-testid="company-gstin-input" value={form.gstin} className="num uppercase"
                onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} placeholder="27AAECS1234C1Z5" />
              <Button type="button" variant="outline" onClick={lookup} data-testid="gstin-lookup-btn">Verify</Button>
            </div>
            {gstInfo?.valid ? (
              <p className="flex items-center gap-1 text-xs text-success" data-testid="gstin-valid-badge">
                <CheckCircle size={13} /> {gstInfo.state} · {gstInfo.taxpayer_type} · Active
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest">PAN</Label>
            <Input data-testid="company-pan-input" value={form.pan} className="num uppercase"
              onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })} placeholder="AAECS1234C" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest">Financial year start</Label>
            <Input type="date" data-testid="company-fy-input" value={form.fy_start}
              onChange={(e) => setForm({ ...form, fy_start: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest">Industry</Label>
            <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
              <SelectTrigger data-testid="company-industry-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest">Registered address</Label>
            <Input data-testid="company-address-input" value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Plot 14, Andheri East, Mumbai" />
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-border pt-4">
          <p className="text-xs text-muted-foreground">21 ledger groups · 26 ledgers · GST duty ledgers · 1 warehouse</p>
          <Button type="submit" disabled={busy} data-testid="create-company-btn">
            {busy ? "Creating…" : "Create company"}
          </Button>
        </div>
      </form>
    </div>
  );
}
